<template>
  <div class="rcs-step rcs-step--carrier-selection">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.subtitle') }}
    </p>
    <p class="rcs-step__hint">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.carrierStarHint') }}
    </p>

    <!-- Loading / error states for the Vonage carrier reference. -->
    <div
      v-if="loading"
      class="rcs-carrier-selection__state"
    >
      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.loading') }}
    </div>
    <div
      v-else-if="error"
      class="rcs-carrier-selection__state rcs-carrier-selection__state--error"
    >
      <span>
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.loadError') }}
      </span>
      <button
        type="button"
        class="rcs-carrier-repeater__add"
        @click="$emit('reload')"
      >
        <i class="fa-solid fa-rotate-right" aria-hidden="true" />
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.retry') }}
      </button>
    </div>

    <template v-else>
      <!-- Country + carrier pickers -->
      <section class="rcs-carrier-section">
        <div class="rcs-form-grid">
          <div class="rcs-form-grid__field">
            <label for="rcs-carrier-country">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.fields.country') }}
            </label>
            <Dropdown
              id="rcs-carrier-country"
              v-model="selectedCountry"
              :options="countryOptions"
              option-label="label"
              option-value="value"
              filter
              :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.placeholders.country')"
            />
          </div>
          <div class="rcs-form-grid__field">
            <label for="rcs-carrier-operators">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.fields.carriers') }}
            </label>
            <div class="rcs-carrier-selection__multiselect">
              <MultiSelect
                id="rcs-carrier-operators"
                v-model="selectedCarrierIdsForCountry"
                :options="carriersForCountry"
                option-label="carrier"
                option-value="id"
                :disabled="!selectedCountry"
                display="chip"
                filter
                append-to="body"
                :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.placeholders.carriers')"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Informational callouts (mirrors the Vonage onboarding screen). -->
      <div class="rcs-carrier-selection__callouts">
        <div class="rcs-step__alert">
          <i class="fa-solid fa-circle-info" aria-hidden="true" />
          <div>
            <p class="rcs-carrier-selection__callout-title">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.callouts.requirements.title') }}
            </p>
            <p>
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.callouts.requirements.body') }}
            </p>
          </div>
        </div>
        <div class="rcs-step__alert rcs-step__alert--warning">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          <div>
            <p class="rcs-carrier-selection__callout-title">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.callouts.fees.title') }}
            </p>
            <p>
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.callouts.fees.body') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Selected carriers -->
      <section class="rcs-carrier-section">
        <h3 class="rcs-carrier-section__title">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.selectedTitle') }}
        </h3>
        <p
          v-if="!selectedCarriers.length"
          class="rcs-carrier-section__lead"
        >
          {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.selectedEmpty') }}
        </p>
        <DataTable
          v-else
          :value="selectedRows"
          :paginator="selectedRows.length > 10"
          :rows="10"
          data-key="id"
          class="rcs-carrier-selection__table"
        >
          <Column
            field="countryLabel"
            :header="t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.table.country')"
          />
          <Column
            field="carrier"
            :header="t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.table.carrier')"
          />
          <Column
            :header="t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.table.actions')"
          >
            <template #body="{ data }">
              <button
                type="button"
                class="rcs-carrier-repeater__remove"
                @click="removeCarrier(data.id)"
              >
                <i class="fa-solid fa-trash-can" aria-hidden="true" />
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrierSelection.table.remove') }}
              </button>
            </template>
          </Column>
        </DataTable>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { countriesList, getCountryName } from '@/components/template-builder/utils/countries-list';
import type { RcsDedicatedFormState, RcsSelectedCarrier } from '@/types/rcs-onboarding';

/** ISO country code → flag emoji, reused from the shared countries list. */
const COUNTRY_FLAGS: Record<string, string> = countriesList.reduce(
  (acc, country) => {
    acc[country.code] = country.flag;
    return acc;
  },
  {} as Record<string, string>,
);

const countryLabel = (code: string): string => {
  const flag = COUNTRY_FLAGS[code] || '';
  return `${flag} ${getCountryName(code)}`.trim();
};

export default defineComponent({
  name: 'RcsCarrierSelectionStep',

  components: {
    Dropdown,
    MultiSelect,
    DataTable,
    Column,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
    availableCarriers: {
      type: Array as PropType<RcsSelectedCarrier[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['reload'],

  setup(props) {
    const { t } = useI18n();
    const selectedCountry = ref<string>('');

    const selectedCarriers = computed(() => props.form.carrier_selection.carriers);

    /** Distinct countries from the reference list, sorted by localized name. */
    const countryOptions = computed(() => {
      const codes = Array.from(
        new Set(props.availableCarriers.map((c) => c.country_letter_code)),
      );
      return codes
        .map((code) => ({ value: code, label: countryLabel(code) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });

    const carriersForCountry = computed(() => props.availableCarriers
      .filter((c) => c.country_letter_code === selectedCountry.value)
      .sort((a, b) => a.carrier.localeCompare(b.carrier)));

    /**
     * Carrier ids selected for the currently displayed country. Writing back
     * merges the country's picks into the global selection without dropping
     * carriers chosen for other countries.
     */
    const selectedCarrierIdsForCountry = computed<string[]>({
      get: () => selectedCarriers.value
        .filter((c) => c.country_letter_code === selectedCountry.value)
        .map((c) => c.id),
      set: (ids) => {
        const others = selectedCarriers.value.filter(
          (c) => c.country_letter_code !== selectedCountry.value,
        );
        const picked = props.availableCarriers.filter(
          (c) => c.country_letter_code === selectedCountry.value && ids.includes(c.id),
        );
        props.form.carrier_selection.carriers = [...others, ...picked];
      },
    });

    const selectedRows = computed(() => selectedCarriers.value.map((c) => ({
      ...c,
      countryLabel: countryLabel(c.country_letter_code),
    })));

    const removeCarrier = (id: string) => {
      props.form.carrier_selection.carriers = selectedCarriers.value.filter(
        (c) => c.id !== id,
      );
    };

    return {
      t,
      selectedCountry,
      countryOptions,
      carriersForCountry,
      selectedCarrierIdsForCountry,
      selectedCarriers,
      selectedRows,
      removeCarrier,
    };
  },
});
</script>
